/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult
} from "react-query";
import { useDispatch } from "react-redux";
import { notificationAdd } from "../../actions/notification";
import useCurrentResourceCentre from "../useCurrentResourceCentre";
import {
  getActiveIPDs,
  getIpds,
  postIpd,
  patchIpd,
  createDischargeSummary,
  createProgressRecord,
  getIpdStats,
  getIpdtDetails
} from "../../api/ipd";
import {
  IPD,
  DischargeSummary,
  TIpdStatus,
  TIpdCase,
  IpdStats,
  ProgressRecord
} from "../../interfaces/IpdInterface";
import { ApiResponse } from "../../interfaces/Api";
import { IPDFormState } from "../../containers/IPD/IpdAdmission/IpdAdmissionCreateEdit";
import { bedsQueryKey, occupiedBedsQueryKey } from "./useBeds";
import { bedAssignmentsQueryKey } from "./useBedAssignments";
import { TWardType } from "../../interfaces/WardInterface";

export const ipdQueryKey = "ipd";
export const ipdsQueryKey = "ipds";
export const activeIpdsQueryKey = "activeIpds";
export const ipdStatsQueryKey = "ipdStats";

// use IPDs (paginated/filtered)
export const useIpds = (props: {
  status: TIpdStatus | string;
  page: number | string;
  pageSize: number | string;
  currentCase?: TIpdCase | string;
  enabled?: boolean;
}): UseQueryResult<ApiResponse<{ total: number; results: IPD[] }>, Error> => {
  const resourceCentre = useCurrentResourceCentre();

  return useQuery({
    queryKey: [
      ipdsQueryKey,
      resourceCentre?.id,
      props.status,
      props.page,
      props.pageSize,
      props.currentCase
    ],
    enabled: (props.enabled ?? true) && !!resourceCentre?.id,
    queryFn: () => getIpds(props)
  });
};

// useGetIpd
export const useGetIpd = (props: { id: number }): UseQueryResult<ApiResponse<IPD>, Error> => {
  const resourceCentre = useCurrentResourceCentre();

  return useQuery({
    queryKey: [ipdQueryKey, resourceCentre?.id, props.id],
    enabled: !!resourceCentre?.id && !!props.id,
    queryFn: () => getIpdtDetails(props.id)
  });
};

// use active IPDs
export const useActiveIpds = (): UseQueryResult<Array<IPD>, Error> => {
  const resourceCentre = useCurrentResourceCentre();

  return useQuery({
    queryKey: [activeIpdsQueryKey, resourceCentre?.id],
    queryFn: async () => {
      if (!resourceCentre?.id) return [];

      const response = await getActiveIPDs();
      return response.data ?? [];
    },
    enabled: !!resourceCentre?.id
  });
};

// use ipd stats
export const useIpdStats = (query: {
  from: string;
  until: string;
  wardType?: TWardType;
}): UseQueryResult<ApiResponse<IpdStats>, Error> => {
  const resourceCentre = useCurrentResourceCentre();

  return useQuery({
    queryKey: [ipdStatsQueryKey, resourceCentre?.id, query.from, query.until, query?.wardType],
    queryFn: () => getIpdStats(query),
    enabled: !!resourceCentre?.id
  });
};

// use post IPD
export const usePostIpd = (): UseMutationResult<ApiResponse<IPD>, Error, IPDFormState, unknown> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const resourceCentre = useCurrentResourceCentre();

  return useMutation({
    mutationFn: (data: IPDFormState) => postIpd(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ipdsQueryKey, resourceCentre?.id] });
      queryClient.invalidateQueries({ queryKey: [activeIpdsQueryKey, resourceCentre?.id] });
      queryClient.invalidateQueries({ queryKey: [occupiedBedsQueryKey, resourceCentre?.id] });
      queryClient.invalidateQueries({ queryKey: [bedAssignmentsQueryKey, resourceCentre?.id] });

      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: "IPD record created successfully",
          variant: "success"
        })
      );
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to create IPD record",
          variant: "error"
        })
      );
    }
  });
};

// use patch IPD
export const usePatchIpd = (): UseMutationResult<
  ApiResponse<IPD>,
  Error,
  { id: number; data: IPDFormState },
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const resourceCentre = useCurrentResourceCentre();

  return useMutation({
    mutationFn: ({ id, data }) => patchIpd(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ipdsQueryKey, resourceCentre?.id] });
      queryClient.invalidateQueries({ queryKey: [activeIpdsQueryKey, resourceCentre?.id] });

      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: "IPD record updated successfully",
          variant: "success"
        })
      );
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to update IPD record",
          variant: "error"
        })
      );
    }
  });
};

// use discharge IPD
export const useDischargeIpd = (): UseMutationResult<
  ApiResponse<IPD>,
  Error,
  { id: number; data: DischargeSummary },
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const resourceCentre = useCurrentResourceCentre();

  return useMutation({
    mutationFn: ({ id, data }) => createDischargeSummary(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ipdsQueryKey, resourceCentre?.id] });
      queryClient.invalidateQueries({ queryKey: [activeIpdsQueryKey, resourceCentre?.id] });
      queryClient.invalidateQueries({ queryKey: [bedsQueryKey, resourceCentre?.id] });
      queryClient.invalidateQueries({ queryKey: [occupiedBedsQueryKey, resourceCentre?.id] });
      queryClient.invalidateQueries({ queryKey: [bedAssignmentsQueryKey, resourceCentre?.id] });

      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: "Patient discharged successfully",
          variant: "success"
        })
      );
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to discharge patient",
          variant: "error"
        })
      );
    }
  });
};

// use post IPD progress record
export const usePostIpdProgressRecord = (): UseMutationResult<
  ApiResponse<IPD>,
  Error,
  { ipdId: number; data: Partial<ProgressRecord> },
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const resourceCentre = useCurrentResourceCentre();

  return useMutation({
    mutationFn: ({ ipdId, data }) => createProgressRecord(ipdId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ipdsQueryKey, resourceCentre?.id] });
      queryClient.invalidateQueries({ queryKey: [activeIpdsQueryKey, resourceCentre?.id] });
      queryClient.invalidateQueries({ queryKey: [occupiedBedsQueryKey, resourceCentre?.id] });

      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: "Progress record created successfully",
          variant: "success"
        })
      );
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to create progress record",
          variant: "error"
        })
      );
    }
  });
};
